import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Row,
  Button
} from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ReactLoader from 'components/Loader';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import useVoiceChannels from 'hooks/useVoiceChannels';

const formSchema = yup.object().shape({
  name: yup
    .string()
    .required('Nome é obrigatório')
    .min(3, 'O nome deve ter no mínimo 3 caracteres'),
  description: yup.string().nullable()
});

const VoiceChannelForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const isUpdate = !!id;

  const [isLoadingData, setIsLoadingData] = useState(isUpdate);
  const { createChannel, updateChannel, getChannelById } = useVoiceChannels();

  const isMountedRef = useRef(true);
  const timeoutRef = useRef(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: '',
      description: ''
    }
  });

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isUpdate || !id) return;

      try {
        const data = await getChannelById(id);

        if (isMounted) {
          if (data) {
            reset({
              name: data.name || '',
              description: data.description || ''
            });
          } else {
            toast.error('Canal de voz não encontrado.');
            history.push('/admin/voice_channels');
          }
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          toast.error('Erro ao carregar dados.');
          history.push('/admin/voice_channels');
        }
      } finally {
        if (isMounted) setIsLoadingData(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, isUpdate, getChannelById, reset, history]);

  const safeNavigate = useCallback(
    (path) => {
      if (isMountedRef.current) {
        history.push(path);
      }
    },
    [history]
  );

  const onSubmit = async (data) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const payload = {
      name: data.name,
      description: data.description
    };

    let success = false;

    try {
      if (isUpdate) {
        success = await updateChannel(id, payload);
      } else {
        success = await createChannel(payload);
      }

      if (success && isMountedRef.current) {
        requestAnimationFrame(() => {
          if (isMountedRef.current) {
            safeNavigate('/admin/voice_channels');
          }
        });
      }
    } catch (error) {
      console.error('Erro no submit:', error);
    }
  };

  const handleCancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    safeNavigate('/admin/voice_channels');
  }, [safeNavigate]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  if (isLoadingData) {
    return (
      <>
        <SimpleHeader name="Carregando..." parentName="Telefonia" />
        <Container className="mt--6" fluid>
          <Row className="justify-content-md-center py-4">
            <ReactLoader />
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <SimpleHeader
        name={isUpdate ? 'Editar Canal de Voz' : 'Novo Canal de Voz'}
        parentName="Telefonia"
        returnPath="/admin/voice_channels"
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Dados do Canal</h3>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div className="pl-lg-4">
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="text"
                            name="name"
                            control={control}
                            label="Nome do Canal*"
                            placeholder="Ex: Vendas, Suporte..."
                            errors={errors}
                            disabled={isSubmitting}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="textarea"
                            name="description"
                            control={control}
                            label="Descrição"
                            placeholder="Breve descrição..."
                            rows={1}
                            errors={errors}
                            disabled={isSubmitting}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <Row>
                      <Col className="text-right">
                        <Button
                          color="secondary"
                          type="button"
                          onClick={handleCancel}
                          className="mr-2"
                          disabled={isSubmitting}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default VoiceChannelForm;
